/** #1264: native contract/share counters, not dollar volume or depth. */
export interface UsMarketStatistics {
  readonly marketSlug: string;
  readonly source: 'public-bbo' | 'market-data-ws';
  readonly counterWindow: 'unspecified';
  readonly observedAtMs: number;
  readonly sharesTraded: string | null;
  readonly openInterest: string | null;
  readonly bidShares: string | null;
  readonly askShares: string | null;
}
const quantity = (value: unknown): string | null => typeof value === 'string' && value.length <= 64
  && /^\d+(?:\.\d+)?$/.test(value) ? value : null;

/** A full observation replaces all fields. Absence/invalid values never become
 * zero or inherit a prior counter. Row ownership supplies current authority;
 * observedAtMs remains the producer's own receipt clock, not a quote clock. */
export function readUsMarketStatistics(value: unknown, marketSlug: string): UsMarketStatistics | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  if ((v.source !== 'public-bbo' && v.source !== 'market-data-ws') || v.counterWindow !== 'unspecified'
    || !Number.isSafeInteger(v.observedAtMs) || (v.observedAtMs as number) <= 0
    || (v.observedAtMs as number) > 8_640_000_000_000_000) return null;
  return Object.freeze({ marketSlug, source: v.source, counterWindow: 'unspecified', observedAtMs: v.observedAtMs as number,
    sharesTraded: quantity(v.sharesTraded), openInterest: quantity(v.openInterest),
    bidShares: quantity(v.bidShares), askShares: quantity(v.askShares) });
}

/** Display abbreviation only; the full exact decimal remains in the tooltip.
 * No Number round-trip, currency inference, delta or accumulation window. */
export function formatUsContractCounter(value: string): string {
  const [rawWhole, fraction = ''] = value.split('.');
  const whole = rawWhole.replace(/^0+(?=\d)/, '');
  if (whole.length <= 3) {
    const visible = fraction.slice(0, 2).replace(/0+$/, '');
    if (whole === '0' && !visible && /[1-9]/.test(fraction)) return '<0.01';
    return whole + (visible ? '.' + visible : '');
  }
  const exponent = Math.min(4, Math.floor((whole.length - 1) / 3));
  if (whole.length > 15) return whole[0] + (whole.slice(1, 3).replace(/0+$/, '') ? '.' + whole.slice(1, 3).replace(/0+$/, '') : '') + 'e' + (whole.length - 1);
  const cut = whole.length - exponent * 3, decimal = whole.slice(cut, cut + 1).replace(/0+$/, '');
  return whole.slice(0, cut) + (decimal ? '.' + decimal : '') + ['', 'K', 'M', 'B', 'T'][exponent];
}
